import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import NotFoundPage from "components/error/NotFoundPage";
import { EmptystateHelmetColored, EmptystateMagnetColored } from "components/icons/generated";
import PageLoading from "components/loading/PageLoading";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import EmptyState from "ds/components/EmptyState";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageStack } from "hooks/useAnalytics/pages/stack";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import useGetAttachedPolicies from "shared/Policy/useGetAttachedPolicies";
import PageInfoHeader from "ds/components/PageInfoHeader";
import Tooltip from "ds/components/Tooltip";
import CollapsibleSection from "ds/components/CollapsibleSection";
import Counter from "ds/components/Counter";

import StackHeader from "../components/Header";
import { StackContext } from "../Context";
import { getStacksBackUrl } from "../helpers";
import { showPoliciesAttachDrawer } from "./AttachDrawer";
import { EMPTY_STATE_CAPTION } from "./constants";
import StackPoliciesListItem from "./ListItem";

const StackPolicies = () => {
  const { stack, canManageStack } = useTypedContext(StackContext);

  const trackSegmentEvent = useAnalytics({
    page: AnalyticsPageStack.StacksPolicies,
  });

  useTitle(`Policies · ${stack.name}`);

  useBreadcrumbs([
    {
      title: "Stacks",
      link: getStacksBackUrl(),
    },
    {
      title: stack.name,
    },
  ]);

  const {
    manuallyAttachedPolicies,
    autoAttachedPolicies,
    allAttachedPoliciesSet,
    isPageLoading,
    isPageNotFound,
    error,
  } = useGetAttachedPolicies({
    entityId: stack.id,
    entityType: "stack",
  });

  const handleAttachAction = () => {
    showPoliciesAttachDrawer({
      entityId: stack.id,
      spaceId: stack.spaceDetails.id,
      alreadyAttachedPolicies: allAttachedPoliciesSet,
    });

    trackSegmentEvent("Attach Policy Clicked");
  };

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (isPageLoading) {
    return <PageLoading />;
  }

  if (isPageNotFound) {
    return <NotFoundPage />;
  }

  return (
    <>
      <StackHeader />
      <PageInfoHeader title="Policies">
        {(canManageStack.result || !!canManageStack.message) && (
          <Tooltip
            active={!!canManageStack.message}
            on={(props) => (
              <Button
                {...props}
                variant="primary"
                onPress={handleAttachAction}
                disabled={!canManageStack.result}
              >
                Attach policy
              </Button>
            )}
          >
            {canManageStack.message}
          </Tooltip>
        )}
      </PageInfoHeader>

      <Box direction="column" gap="x-large">
        <CollapsibleSection
          ariaLevel={3}
          title="Attached directly"
          titleSuffix={<Counter count={manuallyAttachedPolicies.length} />}
          ariaLabel="Attached directly"
          initialIsCollapsed={false}
          contentGap="medium"
        >
          {manuallyAttachedPolicies.map((policy) => (
            <StackPoliciesListItem key={policy.id} policy={policy} />
          ))}

          {manuallyAttachedPolicies.length === 0 && (
            <EmptyState
              padding="large"
              icon={EmptystateHelmetColored}
              title="No policies attached yet."
              caption={EMPTY_STATE_CAPTION}
            />
          )}
        </CollapsibleSection>

        <CollapsibleSection
          ariaLevel={3}
          title="Auto-attached"
          titleSuffix={<Counter count={autoAttachedPolicies.length} />}
          ariaLabel="Auto-attached"
          initialIsCollapsed={false}
          contentGap="medium"
        >
          {autoAttachedPolicies.map((policy) => (
            <StackPoliciesListItem key={policy.id} policy={policy} stackLabels={stack.labels} />
          ))}

          {autoAttachedPolicies.length === 0 && (
            <EmptyState
              padding="large"
              icon={EmptystateMagnetColored}
              title="No policies auto-attached yet."
              caption={EMPTY_STATE_CAPTION}
            />
          )}
        </CollapsibleSection>
      </Box>
    </>
  );
};

export default StackPolicies;
